import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import '../Item/Item.css';

function TableNameIndex() {
    const [data, setData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const { isAuthenticated, companyId, permissions, logout, token } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        fetchtableData();
    }, [isAuthenticated, companyId, token]);

    const fetchtableData = () => {
        fetchData('TableNameApi', '/' + companyId, token)
            .then((r) => { if (r.responseCode === '1111') setData(r.response_data); })
            .catch((e) => console.error('Error fetching Table Name Details:', e));
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((v) => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleDelete = () => {
        deleteData('TableNameApi', '' + deleteItemId, token)
            .then(() => { fetchtableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
            .catch((e) => console.error('Error deleting record:', e));
    };

    const handleActivate = (id) => {
        activateData('TableNameApi', '' + id, token)
            .then(() => fetchtableData())
            .catch((e) => console.error('Error activating record:', e));
    };

    return (
        <div className="item-container">
            <div className="item-header-card">
                <div className="item-header-content">
                    <div className="item-title-section">
                        <span className="item-title-icon"><TableRestaurantIcon fontSize="small" /></span>
                        <h1 className="item-title">Table Name</h1>
                    </div>
                    <div className="item-header-actions">
                        <div className={'item-search-wrapper' + (searchOpen ? ' open' : '')}>
                            <span className="item-search-icon" onClick={() => setSearchOpen(function(o) { return !o; })}>
                                <SearchIcon fontSize="small" />
                            </span>
                            {searchOpen && (
                                <input autoFocus className="item-search-input" type="text" placeholder="Search..."
                                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }} />
                            )}
                        </div>
                        {hasCreateMasterPermission && (
                            <Link to="/tableNameCreate" className="it-btn it-btn-create">
                                <AddIcon style={{ fontSize: 14 }} />&nbsp;Create New
                            </Link>
                        )}
                    </div>
                </div>
            </div>

            <div className="item-table-wrapper">
                <TableContainer component={Paper} className="item-table-container" elevation={0}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className="item-th">Sl.No</TableCell>
                                <TableCell className="item-th">Table Name</TableCell>
                                <TableCell className="item-th">Parcel</TableCell>
                                <TableCell className="item-th">GST N/A</TableCell>
                                <TableCell className="item-th" align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((item, index) => (
                                <TableRow key={item.Id} className={'item-row' + (!item.IsActive ? ' item-row-inactive' : '')}>
                                    <TableCell className="item-td">{index + 1}</TableCell>
                                    <TableCell className="item-td">{item.Name}</TableCell>
                                    <TableCell className="item-td">{item.IsParcel ? 'Yes' : 'No'}</TableCell>
                                    <TableCell className="item-td">{item.GstNotApplicable ? 'Yes' : 'No'}</TableCell>
                                    <TableCell className="item-td" align="center">
                                        <div className="item-action-btns">
                                            {hasEditMasterPermission && (
                                                <Link to={'/tableNameEdit/' + item.Id}>
                                                    <IconButton className="item-icon-btn item-icon-edit" size="small" title="Edit">
                                                        <EditIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Link>
                                            )}
                                            {hasDeleteMasterPermission && (
                                                <IconButton
                                                    className={'item-icon-btn ' + (item.IsActive ? 'item-icon-delete' : 'item-icon-activate')}
                                                    size="small"
                                                    title={item.IsActive ? 'Delete' : 'Activate'}
                                                    onClick={() => item.IsActive ? (setDeleteItemId(item.Id), setConfirmDialogOpen(true)) : handleActivate(item.Id)}
                                                >
                                                    {item.IsActive ? <DeleteIcon sx={{ fontSize: 16 }} /> : <ActivateIcon sx={{ fontSize: 16 }} />}
                                                </IconButton>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} PaperProps={{ style: { borderRadius: '10px', padding: '4px' } }}>
                <DialogTitle style={{ fontSize: '16px', fontWeight: 600 }}>Delete Record?</DialogTitle>
                <DialogContent><DialogContentText style={{ fontSize: '13px' }}>Are you sure you want to delete this record?</DialogContentText></DialogContent>
                <DialogActions style={{ padding: '12px 20px', gap: '8px' }}>
                    <button className="item-dialog-btn it-btn it-btn-cancel" onClick={() => setConfirmDialogOpen(false)}>Cancel</button>
                    <button className="item-dialog-btn it-btn it-btn-back" onClick={handleDelete}>Delete</button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default TableNameIndex;
